import React from 'react';
import { Heart, Users, Target, Shield, BookOpen, Globe } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  const foundationPillars = [
    {
      icon: Heart,
      title: 'Faith & Covenant',
      description: 'Our foundation is built on unwavering faith and covenant values that guide every decision and action we take.'
    },
    {
      icon: Users,
      title: 'Generational Impact',
      description: 'We think beyond today, building programs and structures designed to serve families for generations to come.'
    },
    {
      icon: Target,
      title: 'Excellence & Order',
      description: 'We operate with intentionality, transparency, and a commitment to the highest standards of stewardship.'
    },
    {
      icon: Shield,
      title: 'Dignity & Restoration',
      description: 'Every individual we serve is treated with profound dignity as we walk alongside them toward restoration.'
    }
  ];

  const leadershipValues = [
    {
      icon: BookOpen,
      title: 'Wisdom-Driven',
      description: 'Making decisions rooted in timeless principles and long-term vision.'
    },
    {
      icon: Globe,
      title: 'Community-Centered',
      description: 'Listening to and serving the communities we partner with across the nation.'
    }
  ];

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/at6zaw34_4FF7C568-1205-4E64-9EE8-F0634611DB7F.png"
            alt="About The Parker Family Legacy Foundation"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h1 className="font-serif text-5xl md:text-7xl text-emerald-900 mb-6">About Our Foundation</h1>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>
          <p className="text-xl md:text-2xl text-navy leading-relaxed">
            A legacy institution committed to faith, family, and futures
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-gold mb-8">Our Mission</h2>
          <p className="text-xl md:text-2xl text-ivory leading-relaxed font-light">
            Our mission is to uplift the human race by providing essential support and resources. 
            As a Louisiana-based non-profit, we are dedicated to{' '}
            <span className="font-semibold text-gold">Empowering Lives & Inspiring Change</span>{' '}
            on a global scale.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <div className="h-px w-16 bg-gold mb-4"></div>
                <h2 className="font-serif text-4xl text-emerald-900 mb-4">Our Story</h2>
              </div>
              <p className="text-lg text-navy leading-relaxed mb-6">
                The Parker Family Legacy Foundation was born from a deep conviction that every mother 
                deserves dignity, every child deserves stability, and every family deserves a legacy 
                built on hope and restoration.
              </p>
              <p className="text-lg text-navy leading-relaxed mb-6">
                Founded by Tannesha Parker in 2025, our foundation emerged from personal experience and 
                a calling to create lasting change. We have already begun our mission of service—providing 
                free clothes and essential items to families in need. What started with grassroots community 
                support is evolving into a comprehensive vision for maternal housing, family restoration, 
                and community renewal.
              </p>
              <p className="text-lg text-navy leading-relaxed">
                We are not building a program—we are building an institution. An institution designed 
                to outlive its founders, serve for generations, and transform the landscape of family 
                support across the nation.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/pnrq309j_E259CC80-8178-4D03-BDB6-C1A9EF07CE7C.png"
                alt="Foundation Story"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-gold rounded-sm -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Foundation Pillars */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl md:text-5xl text-emerald-900 mb-6">Our Foundation Pillars</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
            <p className="text-lg text-navy max-w-3xl mx-auto">
              The core principles that define who we are and guide how we serve
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {foundationPillars.map((pillar, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-900 mb-6 group-hover:bg-gold transition-all duration-300">
                  <pillar.icon className="h-8 w-8 text-gold group-hover:text-emerald-900 transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl text-emerald-900 mb-4">{pillar.title}</h3>
                <p className="text-navy/80 leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Banner */}
      <section className="py-16 px-6 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl md:text-2xl text-ivory/90 leading-relaxed italic font-light">
            "From many hands, one people—held together by a God whose love does not divide, but unites. 
            We exist to honor that unity by serving humanity with dignity, compassion, and purpose."
          </p>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl md:text-5xl text-emerald-900 mb-6">Leadership & Governance</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
            <p className="text-lg text-navy max-w-3xl mx-auto">
              Our leadership team brings decades of combined experience in nonprofit management, 
              community development, legal stewardship, and faith-based service.
            </p>
          </div>

          {/* Leadership Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Tannesha Parker */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/ngqu55a8_IMG_6673.jpeg"
                  alt="Tannesha Parker"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Tannesha Parker</h3>
                <p className="text-gold font-semibold mb-4">CEO / Founder</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Provides visionary leadership, organizational direction, and executive oversight—advancing 
                  the Foundation's mission while stewarding legacy, strategy, and long-term impact.
                </p>
              </div>
            </div>

            {/* Erin G Parker */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/2agc0e7d_IMG_6664.jpeg"
                  alt="Erin G Parker"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Erin G Parker</h3>
                <p className="text-gold font-semibold mb-4">Chairman of the Board</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Provides board leadership, governance oversight, and fiduciary accountability, ensuring 
                  the Foundation's mission and values are protected and sustained.
                </p>
              </div>
            </div>

            {/* Sherri Daigle */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/jr1vomz1_IMG_6669.jpeg"
                  alt="Sherri Daigle"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Sherri Daigle</h3>
                <p className="text-gold font-semibold mb-4">Executive Director</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Overseeing daily operations, strategic execution, and mission alignment across all 
                  Foundation initiatives.
                </p>
              </div>
            </div>

            {/* Rene Owens */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/h6njgnwu_IMG_6652.jpeg"
                  alt="Rene Owens"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Rene Owens</h3>
                <p className="text-gold font-semibold mb-4">Treasurer</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Stewarding financial integrity, transparency, and responsible resource management in 
                  service of generational impact.
                </p>
              </div>
            </div>

            {/* Nicholy Toldson-Johnson */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/onimbq0c_IMG_6668.jpeg"
                  alt="Nicholy Toldson-Johnson"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Nicholy Toldson-Johnson</h3>
                <p className="text-gold font-semibold mb-4">Secretary</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Maintaining organizational records, governance documentation, and operational continuity.
                </p>
              </div>
            </div>

            {/* Charlotte Godeau */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/mp4ys25k_IMG_6679.jpeg"
                  alt="Charlotte Godeau"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Charlotte Godeau</h3>
                <p className="text-gold font-semibold mb-4">Board Advisor</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Serves as a trusted advisor to Board and Executive Leadership, offering strategic insight, 
                  ethical guidance, and perspective to support mission alignment and long-term vision.
                </p>
              </div>
            </div>

            {/* Keiara Beverly Fort */}
            <div className="bg-ivory rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/7u3fa5t5_IMG_6682.jpeg"
                  alt="Keiara Beverly Fort"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-emerald-900 mb-2">Keiara Beverly Fort</h3>
                <p className="text-gold font-semibold mb-4">Board Attorney</p>
                <p className="text-navy/80 leading-relaxed text-sm">
                  Providing legal stewardship, ethical oversight, and protection of the Foundation's 
                  mission and governance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Vision */}
      <section className="py-24 px-6 bg-emerald-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block mb-8">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Looking Ahead</h2>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>

          <p className="text-xl text-ivory leading-relaxed mb-12 max-w-4xl mx-auto">
            Our vision extends far beyond today. As a newly established foundation in 2025, we are laying 
            the groundwork for the next century—planning expansion across states, preparing to multiply 
            our impact, and creating a model of excellence that will serve families and communities for 
            generations to come.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
              <h3 className="font-serif text-2xl text-gold mb-4">Launch Parker House</h3>
              <p className="text-ivory/90">
                Establishing our flagship maternity residential program in Baton Rouge, followed by strategic 
                expansion to communities across Louisiana and beyond.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
              <h3 className="font-serif text-2xl text-gold mb-4">Expand Our Service</h3>
              <p className="text-ivory/90">
                Building on our foundation of providing essentials and clothing, developing comprehensive 
                programs in housing, education, and family stabilization.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
              <h3 className="font-serif text-2xl text-gold mb-4">Build Partnerships</h3>
              <p className="text-ivory/90">
                Establishing enduring relationships with donors, institutions, and community leaders committed 
                to joining us in this generational mission.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
